import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import { LogIn, Shield, Globe } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

export default function SuperAdminLogin() {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const { login, user } = useAuth();
  const navigate = useNavigate();

  // Redirect if already logged in as super_admin
  useEffect(() => {
    if (user?.role === 'super_admin') {
      navigate('/superadmin/dashboard');
    }
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Use 'global' as subdomain for super admin login
      const loggedUser = await login(username, password, 'global');
      
      // Only super_admin can login here
      if (loggedUser.role !== 'super_admin') {
        toast.error('Бұл бет тек Super Admin үшін. Мектеп кіру бетін пайдаланыңыз.');
        return;
      }
      
      toast.success(`Қош келдіңіз, ${loggedUser.full_name || 'Super Admin'}!`);
      navigate('/superadmin/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Кіру сәтсіз болды');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left Side - Merit Plus Brand */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden bg-gradient-to-br from-slate-900 via-indigo-950 to-slate-900">
        {/* Animated Background */}
        <div className="absolute inset-0">
          <div className="absolute top-20 left-20 w-72 h-72 bg-indigo-500/20 rounded-full blur-3xl animate-pulse" />
          <div className="absolute bottom-20 right-20 w-96 h-96 bg-purple-500/20 rounded-full blur-3xl animate-pulse" style={{animationDelay: '1s'}} />
          <div className="absolute top-1/2 left-1/2 w-64 h-64 bg-cyan-500/10 rounded-full blur-3xl animate-pulse" style={{animationDelay: '2s'}} />
        </div>
        
        {/* Grid Pattern */}
        <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,.02)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,.02)_1px,transparent_1px)] bg-[size:50px_50px]" />
        
        {/* Content */}
        <div className="relative z-10 flex flex-col items-center justify-center w-full p-12 text-white">
          {/* Logo */}
          <div className="mb-10 relative">
            <div className="w-32 h-32 bg-gradient-to-br from-indigo-500 via-purple-500 to-cyan-500 rounded-3xl flex items-center justify-center shadow-2xl shadow-indigo-500/30">
              <Globe className="w-16 h-16 text-white" />
            </div>
            <div className="absolute -bottom-2 -right-2 w-10 h-10 bg-amber-500 rounded-xl flex items-center justify-center shadow-lg">
              <Shield className="w-5 h-5 text-white" />
            </div>
          </div>
          
          {/* Title */}
          <div className="text-center mb-8">
            <h1 className="text-5xl font-bold mb-3 bg-gradient-to-r from-white via-indigo-200 to-cyan-200 bg-clip-text text-transparent">
              Merit Plus
            </h1>
            <p className="text-xl text-indigo-300 font-medium mb-2">
              Çoklu Okul Yönetim Sistemi
            </p>
            <p className="text-sm text-slate-400 max-w-md">
              Tüm okullarınızı tek bir platformdan yönetin.
              Merit ve Demerit puanlama sistemi.
            </p>
          </div>

          {/* Features */}
          <div className="grid grid-cols-3 gap-6 mt-8">
            <div className="text-center p-4">
              <div className="text-3xl font-bold text-indigo-400">∞</div>
              <div className="text-xs text-slate-400 mt-1">Sınırsız Okul</div>
            </div>
            <div className="text-center p-4">
              <div className="text-3xl font-bold text-purple-400">24/7</div>
              <div className="text-xs text-slate-400 mt-1">Kesintisiz Erişim</div>
            </div>
            <div className="text-center p-4">
              <div className="text-3xl font-bold text-cyan-400">AI</div>
              <div className="text-xs text-slate-400 mt-1">Akıllı Analiz</div>
            </div>
          </div>

          {/* Decorative Lines */}
          <div className="flex items-center space-x-3 mt-12">
            <div className="h-1 w-20 bg-indigo-500 rounded-full" />
            <div className="h-1 w-16 bg-purple-500 rounded-full" />
            <div className="h-1 w-12 bg-cyan-500 rounded-full" />
          </div>
        </div>
      </div>

      {/* Right Side - Login Form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-slate-900">
        <div className="w-full max-w-md">
          {/* Mobile Logo */}
          <div className="lg:hidden text-center mb-8">
            <div className="w-20 h-20 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-xl">
              <Globe className="w-10 h-10 text-white" />
            </div>
            <h2 className="text-3xl font-bold text-white mb-1">Merit Plus</h2>
            <p className="text-indigo-400 font-medium">Super Admin</p>
          </div>

          {/* Form Card */}
          <div className="bg-slate-800 rounded-2xl p-8 space-y-6 border border-slate-700 shadow-2xl relative">
            {/* Accent border */}
            <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-indigo-500 via-purple-500 to-cyan-500 rounded-t-2xl" />
            
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Shield className="w-5 h-5 text-amber-500" />
                <h2 className="text-2xl font-bold text-white">Super Admin Girişi</h2>
              </div>
              <p className="text-slate-400 text-sm">Merit Plus yönetim paneline erişim</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="username" className="text-slate-200 font-semibold">
                  Kullanıcı Adı
                </Label>
                <Input
                  id="username"
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="superadmin"
                  required
                  className="h-12 bg-slate-700 border-slate-600 text-white placeholder:text-slate-500 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 rounded-xl"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="text-slate-200 font-semibold">
                  Şifre
                </Label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  className="h-12 bg-slate-700 border-slate-600 text-white placeholder:text-slate-500 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 rounded-xl"
                />
              </div>

              <Button
                type="submit"
                className="w-full h-12 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white font-bold text-base rounded-xl shadow-lg shadow-indigo-500/25 transition-all"
                disabled={loading}
              >
                {loading ? (
                  <span className="flex items-center justify-center">
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2" />
                    Giriş yapılıyor...
                  </span>
                ) : (
                  <span className="flex items-center justify-center">
                    <LogIn className="mr-2 h-5 w-5" />
                    Giriş Yap
                  </span>
                )}
              </Button>
            </form>

            <div className="text-center pt-4 border-t border-slate-700">
              <p className="text-xs text-slate-500">
                Bu panel sadece Merit Plus Super Adminleri içindir.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
